import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  InputCustomEvent,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import { IDecaDocumentCompany } from '@okcargo-apps/shared-domain';
import { DecaCompanyStoreService } from '../deca-company-store.service';

@Component({
  selector: 'lib-deca-contact-book-modal',
  templateUrl: './deca-contact-book-modal.component.html',
  styleUrls: ['./deca-contact-book-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonLabel,
    IonItem,
    IonContent,
    IonCol,
    IonRow,
    IonGrid,
    IonInput,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    SpinnerLoadingComponent,
  ],
})
export class DecaContactBookModalComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);
  public readonly decaCompanyStoreService = inject(DecaCompanyStoreService);

  @ViewChild('searchInput') searchInput!: IonInput;

  public readonly formSearchContactEdit = this.fb.group({
    searchTerm: [''],
  });

  public readonly searchTerm = toSignal(this.formSearchContactEdit.controls.searchTerm.valueChanges, {
    initialValue: this.formSearchContactEdit.controls.searchTerm.value ?? '',
  });

  // local filtering
  public readonly filteredCompanies = computed(() => {
    const term = this.searchTerm()?.toLowerCase().trim();
    const allCompanies = this.decaCompanyStoreService.companies();

    if (!term) {
      return allCompanies;
    }

    return allCompanies.filter((company) => {
      return (
        company.companyName?.toLowerCase().includes(term) ||
        company.taxId?.toLowerCase().includes(term) ||
        company.contactFirstName?.toLowerCase().includes(term) ||
        company.contactLastName?.toLowerCase().includes(term) ||
        company.contactEmail?.toLowerCase().includes(term)
      );
    });
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchInput?.setFocus();
    }, 100);
  }

  public handleChangeTerm(event: InputCustomEvent): void {
    const value = event.detail.value ?? '';
    this.formSearchContactEdit.controls.searchTerm.setValue(value);
  }

  public handleSelectCompany(company: IDecaDocumentCompany): Promise<boolean> {
    const hasInvalidData = [
      company.companyName,
      company.taxId,
      company.street,
      company.city,
      company.postalCode,
      company.stateCode,
      company.contactEmail,
    ].some((value) => typeof value !== 'string' || value.trim().length === 0);

    if (hasInvalidData) {
      console.error('Invalid company data received from the backend:', company);
      // TODO decide how to surface this error to the user
      return Promise.resolve(false);
    }

    return this.modalCtrl.dismiss(company, 'confirm');
  }

  public closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
